import { ReactNode, useEffect, useState } from "react";
import { Main } from "@/components/Main";
import { TableData } from "@/App/ContestProfits/Table";
import { getContestProfits, getSelectContests, exportContestProfitsSummary } from "@/Api/contests";
import { getSelectProductGroups } from "@/Api/productGroups";
import { ContestProfitResponse } from "@/types/contestProfit";
import { TrendingUp, TrendingDown, DollarSign, Trophy, Clock, Percent, Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { Mask } from "@/utils/mask";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { MultiSelectCheckbox } from "@/components/ui/multi-select-checkbox";

const getFirstDayOfMonth = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
};

const getLastDayOfMonth = () => {
  const date = new Date();
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, "0")}-${String(lastDay.getDate()).padStart(2, "0")}`;
};

const formatSignedCurrency = (value: number) =>
  `${value >= 0 ? "+" : ""}${Mask.formatCurrency(value)}`;

const resultColor = (value: number) =>
  value >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400";

const resultIconBg = (value: number) =>
  value >= 0 ? "bg-green-100 dark:bg-green-900/20" : "bg-red-100 dark:bg-red-900/20";

interface SummaryCardProps {
  title: string;
  value: ReactNode;
  valueClassName?: string;
  icon: ReactNode;
  iconClassName: string;
  hint?: ReactNode;
  extra?: ReactNode;
}

/** Card de resumo: título e ícone em cima, valor embaixo, para caber 5 lado a lado */
const SummaryCard = ({ title, value, valueClassName, icon, iconClassName, hint, extra }: SummaryCardProps) => (
  <Card>
    <CardContent className="p-5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
        <div className={cn("h-9 w-9 shrink-0 rounded-full flex items-center justify-center", iconClassName)}>
          {icon}
        </div>
      </div>
      <p
        className={cn(
          "text-xl font-bold mt-2 whitespace-nowrap tabular-nums text-gray-900 dark:text-gray-100",
          valueClassName
        )}
      >
        {value}
      </p>
      {hint && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{hint}</div>}
      {extra}
    </CardContent>
  </Card>
);

export const PageList = () => {
  const [listData, setListData] = useState<ContestProfitResponse>({
    current_page: 1,
    data: [],
    first_page_url: "",
    from: 0,
    last_page: 1,
    last_page_url: "",
    next_page_url: null,
    path: "",
    per_page: 15,
    prev_page_url: null,
    to: 0,
    total: 0,
  });
  const [state, setState] = useState("loading");
  const [lotteries, setLotteries] = useState<Array<{ id: number; name: string }>>([]);
  const [contests, setContests] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedLotteries, setSelectedLotteries] = useState<string[]>([]);
  const [selectedContests, setSelectedContests] = useState<string[]>([]);
  const [validationStartDate, setValidationStartDate] = useState<string>(getFirstDayOfMonth());
  const [validationEndDate, setValidationEndDate] = useState<string>(getLastDayOfMonth());

  const [exporting, setExporting] = useState(false);

  const handleExport = async (format: "xlsx" | "pdf") => {
    setExporting(true);
    try {
      const blob = await exportContestProfitsSummary(format, {
        lottery_id: selectedLotteries.length > 0 ? selectedLotteries : undefined,
        contest_id: selectedContests.length > 0 ? selectedContests : undefined,
        validation_start_date: validationStartDate || undefined,
        validation_end_date: validationEndDate || undefined,
      });
      if (!blob) return;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `lucros-concursos-${new Date().toISOString().split("T")[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(format === "pdf" ? "PDF baixado!" : "Planilha baixada!");
    } catch (error) {
      console.error("Erro ao exportar lucros do concurso:", error);
      toast.error("Erro ao exportar");
    } finally {
      setExporting(false);
    }
  };

  const fetchData = async (
    page: number = 1,
    lotteryIds: string[] = [],
    contestIds: string[] = [],
    valStartDate?: string,
    valEndDate?: string
  ) => {
    setState("loading");
    try {
      const { data, ok } = await getContestProfits(page, {
        lottery_id: lotteryIds.length > 0 ? lotteryIds : undefined,
        contest_id: contestIds.length > 0 ? contestIds : undefined,
        validation_start_date: valStartDate || undefined,
        validation_end_date: valEndDate || undefined,
      });

      if (ok && data?.response) {
        setListData(data.response);
      }
    } catch (error) {
      console.error("Error fetching contest profits:", error);
    } finally {
      setState("production");
    }
  };

  const fetchLotteries = async () => {
    try {
      const { data, ok } = await getSelectProductGroups();
      if (ok && data?.response) {
        setLotteries(data.response);
      }
    } catch (error) {
      console.error("Error fetching lotteries:", error);
    }
  };

  const fetchContests = async (lotteryIds?: string[]) => {
    try {
      const lotteryIdNumber = lotteryIds && lotteryIds.length === 1 ? Number(lotteryIds[0]) : undefined;
      const { data, ok } = await getSelectContests(lotteryIdNumber);
      if (ok && data?.response) {
        setContests(data.response);
      }
    } catch (error) {
      console.error("Error fetching contests:", error);
    }
  };

  useEffect(() => {
    fetchData(1, [], [], getFirstDayOfMonth(), getLastDayOfMonth());
    fetchLotteries();
    fetchContests();
  }, []);

  useEffect(() => {
    if (selectedLotteries.length > 0) {
      fetchContests(selectedLotteries);
    } else {
      fetchContests();
    }
  }, [selectedLotteries]);

  const pageActions = {
    next: async () => {
      if (listData.current_page < listData.last_page) {
        await fetchData(listData.current_page + 1, selectedLotteries, selectedContests, validationStartDate, validationEndDate);
      }
    },
    previous: async () => {
      if (listData.current_page > 1) {
        await fetchData(listData.current_page - 1, selectedLotteries, selectedContests, validationStartDate, validationEndDate);
      }
    },
    goToPage: async (page: number) => {
      await fetchData(page, selectedLotteries, selectedContests, validationStartDate, validationEndDate);
    },
  };

  const handleApplyFilter = () => {
    fetchData(1, selectedLotteries, selectedContests, validationStartDate, validationEndDate);
  };

  const handleClearFilter = () => {
    setSelectedLotteries([]);
    setSelectedContests([]);
    setValidationStartDate(getFirstDayOfMonth());
    setValidationEndDate(getLastDayOfMonth());
    fetchData(1, [], [], getFirstDayOfMonth(), getLastDayOfMonth());
    fetchContests();
  };

  return (
    <Main>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-semibold flex gap-2 items-center">
            <TrendingUp className="h-6 w-6" />
            Lucros do Concurso
          </h2>
        </div>

        {/* Filtros */}
        <div className="bg-white dark:bg-[#2c2c2c] p-4 rounded-lg border">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
            <div className="space-y-2">
              <Label htmlFor="validation_start_date">Data Validação Início</Label>
              <Input
                id="validation_start_date"
                type="date"
                value={validationStartDate}
                onChange={(e) => setValidationStartDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="validation_end_date">Data Validação Fim</Label>
              <Input
                id="validation_end_date"
                type="date"
                value={validationEndDate}
                onChange={(e) => setValidationEndDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lottery-filter">Loteria</Label>
              <MultiSelectCheckbox
                options={lotteries.map((lottery) => ({
                  value: lottery.id.toString(),
                  label: lottery.name,
                }))}
                selectedValues={selectedLotteries}
                onSelectionChange={setSelectedLotteries}
                placeholder="Selecione a loteria"
                selectAllLabel="SELECIONAR TUDO"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contest-filter">Concurso</Label>
              <MultiSelectCheckbox
                options={contests.map((contest) => ({
                  value: contest.id.toString(),
                  label: contest.name,
                }))}
                selectedValues={selectedContests}
                onSelectionChange={setSelectedContests}
                placeholder="Selecione o concurso"
                selectAllLabel="SELECIONAR TUDO"
              />
            </div>

            <div className="flex gap-2 md:col-span-2">
              <Button
                onClick={handleApplyFilter}
                className={cn(colors.bg_primary, "flex-1")}
              >
                Aplicar Filtros
              </Button>
              <Button
                variant="outline"
                onClick={handleClearFilter}
                className="flex-1"
              >
                Limpar
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" className="flex-1" disabled={exporting}>
                    {exporting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Exportar
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem className="cursor-pointer" onClick={() => handleExport("xlsx")}>
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Excel
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => handleExport("pdf")}>
                    <FileText className="h-4 w-4 mr-2" />
                    PDF
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Cards de Resumo */}
        {state !== "loading" && listData?.totals && (() => {
          const totals = listData.totals;
          const profit = totals.total_general_profit || 0;
          const unsoldPrize = totals.total_general_unsold_prize || 0;
          const finalProfit = totals.total_general_final_profit ?? profit + unsoldPrize;
          const investigation = totals.investigation;
          const investigationStatus = investigation?.status ?? "pending";
          const investigationProgress = investigation
            ? `${investigation.investigated_products} de ${investigation.total_products} bolões apurados`
            : "";

          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              <SummaryCard
                title="Total Vendido"
                value={Mask.formatCurrency(totals.total_general_revenue || 0)}
                icon={<DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />}
                iconClassName="bg-green-100 dark:bg-green-900/20"
                hint={`${totals.total_quotas_sold || 0} cotas vendidas`}
                extra={
                  (totals.total_quotas_sold_with_discount || 0) > 0 && (
                    <div className="mt-2 pt-2 border-t text-xs">
                      <p className="flex items-center gap-1 text-purple-600 dark:text-purple-400">
                        <Percent className="h-3 w-3" />
                        {totals.total_quotas_sold_with_discount} cotas com desconto
                      </p>
                      <p className="text-red-600 dark:text-red-400">
                        -{Mask.formatCurrency(totals.total_discount_value || 0)} em descontos
                      </p>
                    </div>
                  )
                }
              />

              <SummaryCard
                title="Custo Total"
                value={Mask.formatCurrency(totals.total_general_cost || 0)}
                icon={<TrendingDown className="h-5 w-5 text-red-600 dark:text-red-400" />}
                iconClassName="bg-red-100 dark:bg-red-900/20"
                hint={`${totals.total_quotas_not_sold || 0} cotas não vendidas`}
              />

              <SummaryCard
                title="Resultado sem prêmios"
                value={formatSignedCurrency(profit)}
                valueClassName={resultColor(profit)}
                icon={<TrendingUp className={cn("h-5 w-5", resultColor(profit))} />}
                iconClassName={resultIconBg(profit)}
                hint="Lucro (vendido - custo)"
              />

              <SummaryCard
                title="Premiações"
                value={
                  investigationStatus === "pending" ? (
                    <span className="flex items-center gap-1.5 text-base text-gray-400 dark:text-gray-500">
                      <Clock className="h-4 w-4 shrink-0" />
                      Apuração pendente
                    </span>
                  ) : (
                    Mask.formatCurrency(unsoldPrize)
                  )
                }
                valueClassName={
                  investigationStatus === "pending"
                    ? "whitespace-normal"
                    : unsoldPrize > 0
                      ? "text-green-600 dark:text-green-400"
                      : undefined
                }
                icon={<Trophy className="h-5 w-5 text-amber-600 dark:text-amber-400" />}
                iconClassName="bg-amber-100 dark:bg-amber-900/20"
                hint={
                  <>
                    <span className="block">
                      {totals.total_unsold_prize_quotas || 0} cotas não vendidas premiadas
                    </span>
                    {investigationProgress && (
                      <span className="block text-amber-600 dark:text-amber-400">
                        {investigationProgress}
                      </span>
                    )}
                  </>
                }
              />

              <SummaryCard
                title="Resultado final"
                value={formatSignedCurrency(finalProfit)}
                valueClassName={resultColor(finalProfit)}
                icon={<TrendingUp className={cn("h-5 w-5", resultColor(finalProfit))} />}
                iconClassName={resultIconBg(finalProfit)}
                hint={
                  investigationStatus === "completed"
                    ? "Com as premiações"
                    : "Parcial: aguardando apuração"
                }
              />
            </div>
          );
        })()}

        {/* Tabela */}
        <TableData
          dataApi={listData}
          pageActions={pageActions}
          state={state}
        />
      </div>
    </Main>
  );
};
